
'use client';

import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getQuotation, type GenerateQuotationOutput, type GenerateQuotationInput } from './actions';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, HeadingLevel, AlignmentType } from 'docx';


import { GradientText } from '@/components/ui/gradient-text';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, Sparkles, FileSignature, ListChecks, DollarSign, Clock, CheckSquare, Download, FileText, FileDown, Route, Monitor, Layers3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Table as UiTable, TableBody, TableCell as UiTableCell, TableHead, TableHeader, TableRow as UiTableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';

const formSchema = z.object({
  projectDescription: z.string().min(20, { message: 'Please provide a more detailed project description.' }),
});

export default function QuotationGeneratorPage() {
  const [result, setResult] = useState<GenerateQuotationOutput | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const quotationResultRef = useRef<HTMLDivElement>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectDescription: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setResult(null);
    const response = await getQuotation(values);
    setIsLoading(false);

    if (response.success && response.data) {
      setResult(response.data);
    } else {
      toast({
        variant: 'destructive',
        title: 'Error Generating Quotation',
        description: response.message || 'An unexpected error occurred. Please try again.',
      });
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  
  const handleDownloadPdf = async () => {
    const element = quotationResultRef.current;
    if (!element) return;
    const canvas = await html2canvas(element, { scale: 2 });
    const data = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 0;

    pdf.addImage(data, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`${result?.projectName || 'quotation'}.pdf`);
  };

  const handleDownloadDocx = async () => {
    if (!result) return;
    
    const doc = new Document({
        sections: [{
            properties: {},
            children: [
                new Paragraph({ text: result.projectName, heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER }),
                new Paragraph({ text: "Project Quotation", heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER }),
                new Paragraph({ text: "" }), // Spacer
                new Paragraph({ text: "Scope of Work", heading: HeadingLevel.HEADING_1 }),
                new Paragraph(result.scopeOfWork),
                new Paragraph({ text: "" }),
                 new Paragraph({ text: "Application Flow", heading: HeadingLevel.HEADING_1 }),
                new Paragraph(result.appFlow),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "Screens", heading: HeadingLevel.HEADING_1 }),
                ...result.screens.flatMap(screen => [
                    new Paragraph({ text: screen.name, heading: HeadingLevel.HEADING_2 }),
                    new Paragraph(screen.description),
                    new Paragraph(""),
                ]),
                new Paragraph({ text: "Technology Stack", heading: HeadingLevel.HEADING_1 }),
                ...result.techStack.flatMap(stack => [
                     new Paragraph({ text: stack.category, heading: HeadingLevel.HEADING_2 }),
                     new Paragraph(stack.technologies.join(', ')),
                     new Paragraph(""),
                ]),
                new Paragraph({ text: "Cost Breakdown", heading: HeadingLevel.HEADING_1 }),
                new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    rows: [
                        new TableRow({
                            children: [
                                new TableCell({ width: { size: 40, type: WidthType.PERCENTAGE }, children: [new Paragraph({ text: "Feature", style: "strong" })] }),
                                new TableCell({ width: { size: 40, type: WidthType.PERCENTAGE }, children: [new Paragraph({ text: "Description", style: "strong" })] }),
                                new TableCell({ width: { size: 20, type: WidthType.PERCENTAGE }, children: [new Paragraph({ text: "Cost", style: "strong", alignment: AlignmentType.RIGHT })] }),
                            ],
                        }),
                        ...result.costBreakdown.flatMap(item => new TableRow({
                            children: [
                                new TableCell({ children: [new Paragraph(item.item)] }),
                                new TableCell({ children: [new Paragraph(item.description)] }),
                                new TableCell({ children: [new Paragraph({ text: formatCurrency(item.cost), alignment: AlignmentType.RIGHT })] }),
                            ],
                        })),
                    ],
                }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: `Estimated Timeline: ${result.timeline.duration}`, heading: HeadingLevel.HEADING_1 }),
                 ...result.timeline.phases.flatMap(phase => [
                    new Paragraph({
                        children: [
                            new TextRun({ text: `${phase.name}: `, bold: true }),
                            new TextRun(phase.duration),
                        ],
                    }),
                ]),
                new Paragraph({ text: "" }),
                 new Paragraph({
                    heading: HeadingLevel.HEADING_2,
                    alignment: AlignmentType.RIGHT,
                    children: [
                        new TextRun(`Total Estimated Cost: ${formatCurrency(result.totalCost)}`),
                    ],
                }),
                new Paragraph({ text: "" }),
                new Paragraph({ text: "Assumptions", heading: HeadingLevel.HEADING_1 }),
                ...result.assumptions.map(assumption => new Paragraph({ text: assumption, bullet: { level: 0 } })),
            ],
        }],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.projectName || 'quotation'}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  return (
    <div className="container mx-auto max-w-5xl py-12 px-4 print:py-0">
      <div className="text-center mb-12 print:hidden">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          AI Quotation <GradientText>Generator</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          Describe your project, and our AI will generate a detailed quotation to help you estimate costs and timelines.
        </p>
      </div>

      <Card className="max-w-3xl mx-auto print:hidden">
        <CardContent className="p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="projectDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea 
                        placeholder="e.g., 'Build a mobile app for iOS and Android for a restaurant. Users should be able to view the menu, place orders for delivery, and make reservations. Include a simple admin panel for the restaurant to manage the menu and orders.'" 
                        {...field} 
                        rows={8} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating Quotation...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Quotation
                  </>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {result && (
        <div className="mt-8 animate-in fade-in-50 max-w-5xl mx-auto">
            <div id="quotation-result" ref={quotationResultRef} className="bg-card p-8 print:p-0">
                <Card className="print:shadow-none print:border-none">
                <CardHeader>
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                            <FileSignature className="text-primary h-12 w-12 flex-shrink-0" />
                            <div>
                                <CardTitle className="font-headline text-3xl">{result.projectName}</CardTitle>
                                <CardDescription className="text-lg">Project Quotation</CardDescription>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-8">
                    {/* Scope of Work */}
                    <div>
                        <h3 className="font-headline text-xl flex items-center gap-2 mb-2"><ListChecks className="text-primary"/>Scope of Work</h3>
                        <p className="text-muted-foreground">{result.scopeOfWork}</p>
                    </div>
                    <Separator />
                    {/* App Flow */}
                     <div>
                        <h3 className="font-headline text-xl flex items-center gap-2 mb-2"><Route className="text-primary"/>Application Flow</h3>
                        <p className="text-muted-foreground">{result.appFlow}</p>
                    </div>
                    <Separator />
                    {/* Screens */}
                    <div>
                        <h3 className="font-headline text-xl flex items-center gap-2 mb-4"><Monitor className="text-primary"/>Basic Screens</h3>
                         <div className="grid md:grid-cols-2 gap-4">
                            {result.screens.map((screen, index) => (
                                <Card key={index} className="bg-card-foreground/5 p-4">
                                <p className="font-semibold">{screen.name}</p>
                                <p className="text-muted-foreground">{screen.description}</p>
                                </Card>
                            ))}
                        </div>
                    </div>
                    <Separator />
                    {/* Tech Stack */}
                     <div>
                        <h3 className="font-headline text-xl flex items-center gap-2 mb-4"><Layers3 className="text-primary"/>Technology Stack</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            {result.techStack.map((stack, index) => (
                                <Card key={index} className="bg-card-foreground/5 p-4">
                                <p className="font-semibold">{stack.category}</p>
                                <p className="text-muted-foreground">{stack.technologies.join(', ')}</p>
                                </Card>
                            ))}
                        </div>
                    </div>
                    <Separator />
                    {/* Cost Breakdown */}
                    <div>
                        <h3 className="font-headline text-xl flex items-center gap-2 mb-4"><DollarSign className="text-primary"/>Cost Breakdown</h3>
                        <UiTable>
                            <TableHeader>
                            <UiTableRow>
                                <UiTableCell className="w-[40%]">Feature</UiTableCell>
                                <UiTableCell>Description</UiTableCell>
                                <UiTableCell className="text-right">Cost</UiTableCell>
                            </UiTableRow>
                            </TableHeader>
                            <TableBody>
                            {result.costBreakdown.map((item, index) => (
                                <UiTableRow key={index}>
                                <UiTableCell className="font-medium">{item.item}</UiTableCell>
                                <UiTableCell className="text-muted-foreground">{item.description}</UiTableCell>
                                <UiTableCell className="text-right font-mono">{formatCurrency(item.cost)}</UiTableCell>
                                </UiTableRow>
                            ))}
                            </TableBody>
                        </UiTable>
                    </div>
                    <Separator />
                    {/* Timeline */}
                    <div>
                        <h3 className="font-headline text-xl flex items-center gap-2 mb-4"><Clock className="text-primary"/>Estimated Timeline: {result.timeline.duration}</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            {result.timeline.phases.map((phase, index) => (
                                <Card key={index} className="bg-card-foreground/5 p-4">
                                <p className="font-semibold">{phase.name}</p>
                                <p className="text-muted-foreground">{phase.duration}</p>
                                </Card>
                            ))}
                        </div>
                    </div>
                    <Separator />
                    {/* Total */}
                    <div className="flex justify-end items-center gap-4 text-2xl font-bold">
                    <span>Total Estimated Cost:</span>
                    <span className="text-primary font-mono">{formatCurrency(result.totalCost)}</span>
                    </div>
                    <Separator />
                    {/* Assumptions */}
                    <div>
                        <h3 className="font-headline text-xl flex items-center gap-2 mb-4"><CheckSquare className="text-primary"/>Assumptions</h3>
                        <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                            {result.assumptions.map((assumption, index) => (
                                <li key={index}>{assumption}</li>
                            ))}
                        </ul>
                    </div>
                </CardContent>
                </Card>
            </div>
            <div className="flex justify-end gap-4 mt-4 print:hidden">
              <Button onClick={handleDownloadPdf} variant="outline">
                  <FileDown className="mr-2 h-4 w-4" />
                  Download PDF
              </Button>
              <Button onClick={handleDownloadDocx} variant="outline">
                  <FileText className="mr-2 h-4 w-4" />
                  Download DOCX
              </Button>
            </div>
        </div>
      )}
    </div>
  );
}
